import { isMcpToolName } from '@terra/shared-chat-trace/trace-view/utils/tool-config.helpers';
import { normalizeToolName } from '../utils/tool-config';
import { isMongoId, detectEntityType } from '../context/EntityResolverContext';

const TOOLS_WITHOUT_COMPACT_PREVIEW = new Set([
  'end-test',
  'todo-write',
  'save-neo-memories',
  'ai-discovery-complete',
  'ai-discovery-verdict',
]);

const TOOLS_WITH_QUERY_PREVIEW = new Set([
  'search-endpoints',
  'ask-codebase',
  'coder',
  'sub-agent-coder',
  'sub-agent-intelligence',
  'navigate-web',
  'get-neo-memories',
  'business-context-tool',
  'get-business-context',
]);

const INTERNAL_NETWORK_TOOLS_WITH_ARGS_PREVIEW = new Set([
  'remote-c2-command',
  'list-ad-entities',
  'get-ad-entity-details',
  'get-ad-entity-relationships',
  'query-ad-graph',
  'create-internal-network-discovery-signal',
]);

const SERVER_INJECTED_MCP_ARG_KEYS = new Set(['surfaceId', 'executionId']);

export function filterMcpDisplayArgs({
  toolName,
  args,
}: {
  toolName: string;
  args: Record<string, unknown>;
}): Record<string, unknown> {
  if (!isMcpToolName(toolName)) {
    return args;
  }

  return Object.fromEntries(
    Object.entries(args).filter(
      ([key]) => !SERVER_INJECTED_MCP_ARG_KEYS.has(key),
    ),
  );
}

const FILE_UPLOAD_TRUNCATED_ARG_KEYS = new Set([
  'embeddedText',
  'embedded_text',
]);

export function filterFileUploadDisplayArgs({
  toolName,
  args,
}: {
  toolName: string;
  args: Record<string, unknown>;
}): Record<string, unknown> {
  const normalized = normalizeToolName(toolName);
  if (normalized !== 'file-upload-client' && normalized !== 'prepare-file') {
    return args;
  }

  return Object.fromEntries(
    Object.entries(args).map(([key, value]) => {
      if (
        FILE_UPLOAD_TRUNCATED_ARG_KEYS.has(key) &&
        typeof value === 'string' &&
        value.length > 48
      ) {
        return [key, `${value.slice(0, 48)}...`];
      }
      return [key, value];
    }),
  );
}

function hasInternalNetworkArgsPreview(
  normalized: string,
  args: Record<string, unknown>,
): boolean {
  if (!INTERNAL_NETWORK_TOOLS_WITH_ARGS_PREVIEW.has(normalized)) {
    return false;
  }

  if (normalized === 'remote-c2-command') {
    return Boolean(args.command);
  }
  if (normalized === 'list-ad-entities') {
    return Boolean(args.entityKind);
  }
  if (normalized === 'query-ad-graph') {
    return Boolean(args.mode);
  }
  if (
    normalized === 'get-ad-entity-details' ||
    normalized === 'get-ad-entity-relationships'
  ) {
    return Boolean(args.nodeId);
  }
  if (normalized === 'create-internal-network-discovery-signal') {
    return Boolean(args.description);
  }

  return false;
}

function hasMcpArgsPreview(args: Record<string, unknown>): boolean {
  return Boolean(args.query ?? args.term ?? args.mode);
}

export function hasCompactArgsPreview(
  toolName: string,
  args: Record<string, unknown>,
): boolean {
  if (!args || Object.keys(args).length === 0) {
    return false;
  }

  const normalized = normalizeToolName(toolName);
  const displayArgs = filterMcpDisplayArgs({ toolName, args });

  if (Object.keys(displayArgs).length === 0) {
    return false;
  }

  if (TOOLS_WITHOUT_COMPACT_PREVIEW.has(normalized)) {
    return false;
  }

  if (TOOLS_WITH_QUERY_PREVIEW.has(normalized)) {
    return Boolean(displayArgs.query);
  }

  if (hasInternalNetworkArgsPreview(normalized, displayArgs)) {
    return true;
  }

  if (isMcpToolName(toolName) && hasMcpArgsPreview(displayArgs)) {
    return true;
  }

  if (
    normalized === 'http-tool' ||
    normalized === 'dynamic-http-tool' ||
    normalized === 'ai-discovery-http' ||
    normalized === 'ai-discovery-fetch-openapi'
  ) {
    return true;
  }

  if (displayArgs.endpointId) {
    return true;
  }
  if (displayArgs.credentialId) {
    return true;
  }

  for (const [key, value] of Object.entries(displayArgs)) {
    if (isMongoId(value) && detectEntityType(key)) {
      return true;
    }
  }

  return false;
}

export function truncateArgsPreview(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
}
